import {Injectable} from '@angular/core';
import {ApiServiceService} from '../api-service.service';
import {LoanApplication} from '../model/loan-application.model';
import {StorageService} from './storage.service';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Loan} from '../model/loan.model';
import {AdvanceEmiPlanService} from './advance-emi-plan.service';
import {AdvanceEmiPlan} from '../model/AdvanceEmiPlan';
import {LoanStatusType} from '../model/loan-status.model';
import {SortingOrder} from '../model/common/Sorting';

@Injectable()
export class LoanDetailsService {
    private apiService: ApiServiceService;
    private http: HttpClient;
    private storageService: StorageService;

    constructor(apiService: ApiServiceService, http: HttpClient, storageService: StorageService, private advanceEmiPlanService: AdvanceEmiPlanService) {
        this.apiService = apiService;
        this.http = http;
        this.storageService = storageService;
    }

    async getLoanDetails(loanId: string): Promise<any> {
        const url = this.apiService.baseURL() + 'getLoanInfoDetails';
        return await this.http.post(url, {loan_id: loanId}).toPromise();
    }

    async getLoanDetailsByApplicationId(applicationId: string): Promise<any> {
        const url = this.apiService.baseURL() + `loan?loanApplicationId=${applicationId}`;
        return await this.http.get(url).toPromise();
    }

    public async getLoansByUserId(userId: string): Promise<Loan[]> {
        const options = {params: new HttpParams().set('userId', userId)};
        return this.http.get<Loan[]>(`${this.apiService.baseURL()}loans`, options).toPromise();
    }

    async createLoanApplication(loanApplication: LoanApplication): Promise<any> {
        const url = this.apiService.baseURL() + 'loan';

        const response = await this.http.post<{ loanId: string }>(url, loanApplication).toPromise();
        const advanceEmiPlan = loanApplication.advanceEmiPlan;
        if (advanceEmiPlan) {
            const createdLoanId = response['loanId'];
            const advanceEmiPlanToSave = AdvanceEmiPlan.from({
                requestedAmount: advanceEmiPlan.requestedAmount,
                advanceEmiOption: advanceEmiPlan.advanceEmiOption,
                loanId: createdLoanId
            });
            await this.advanceEmiPlanService.create(advanceEmiPlanToSave);
        }
        return response;
    }

    async updateResidenceProof(proofType: string, proofNumber: string, loanId: string, loanApplicationComplete: number): Promise<void> {
        const url = this.apiService.baseURL() + 'updateResidenceProof';
        const response = await this.http.post<{ status: string, status_code: number }>(url, {
            proof_type: proofType,
            proof_no: proofNumber,
            loan_id: loanId,
            loan_application_complete: loanApplicationComplete
        }).toPromise();
        if (response.status !== 'success' || response.status_code !== 201) {
            throw new Error(`Encountered an error while updating residence proof. ${JSON.stringify(response)}`);
        }
    }

    public async getDelayedLoansFor(lenderId: string): Promise<Loan[]> {
        const url = `${this.apiService.baseURL()}loans?lenderId=${lenderId}&emiPaymentStatus=delayed`;
        return await this.http.get<Loan[]>(url).toPromise();
    }

    async getCitiesWhereLoanIsOffered(): Promise<any []> {
        const response = await this.http.post<{ status: string, result: { city: { city: string }[] } }>(this.apiService.baseURL() + 'getAllSettings', {})
            .toPromise();

        if (response.status === 'success') {
            return response['result']['city'];
        }
    }

    public async getWrittenOffLoansFor(lenderId: string): Promise<Loan[]> {
        const url = `${this.apiService.baseURL()}loans?lenderId=${lenderId}&emiPaymentStatus=written_off`;
        return await this.http.get<Loan[]>(url).toPromise();
    }

    public async getSettledLoansFor(lenderId: string): Promise<Loan[]> {
        const url = `${this.apiService.baseURL()}loans?lenderId=${lenderId}&emiPaymentStatus=settlement`;
        return await this.http.get<Loan[]>(url).toPromise();
    }

    public async getRestructuredLoansFor(lenderId: string): Promise<Loan[]> {
        const url = `${this.apiService.baseURL()}loans?lenderId=${lenderId}&emiPaymentStatus=restructured`;
        return await this.http.get<Loan[]>(url).toPromise();
    }

    public async getAllocatedLoansFor(lenderId: string): Promise<Loan[]> {
        const url = `${this.apiService.baseURL()}loans?lenderId=${lenderId}&loanSanctionStatus=${LoanStatusType.Accepted}&loanSanctionStatus=${LoanStatusType.QueuedToBeFulfilled}`;
        return await this.http.get<Loan[]>(url).toPromise();
    }

    public async getLoansByStatuses(statuses: LoanStatusType[], sortBy?: string, sortOrder?: SortingOrder, limit?: number): Promise<Loan[]> {
        let params = new HttpParams();
        statuses.forEach(status => {
            params = params.append('loanSanctionStatus', status);
        });
        if (sortBy) {
            params = params.append('sortBy', sortBy);
        }
        if (sortOrder) {
            params = params.append('sortOrder', sortOrder);
        }
        if (limit) {
            params = params.append('limit', limit.toString());
        }
        const url = `${this.apiService.baseURL()}loans`;
        return await this.http.get<Loan[]>(url, { params: params }).toPromise();
    }

    public async getLenderInvestedInfo(loanId: string, lenderId: string): Promise<any> {
        const url = `${this.apiService.baseURL()}loan/${loanId}/lender/${lenderId}`;
        return await this.http.get(url).toPromise();
    }

    async getPartnerByGeneratedLoanId(generatedLoanId: string): Promise<any> {
        const url = this.apiService.baseURL() + `loan/partner?generatedLoanId=${generatedLoanId}`;
        return await this.http.get(url).toPromise();
    }
}
